import React, { useCallback } from 'react';

import { createStyles, Group, Paper, Skeleton, Text, Title, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import BackButton from 'components/BackButton';
import DownloadCSV from 'components/DownloadCSV';
import PackageMultiSelect from 'components/form-fields/auto-complete-fields/PackageMultiSelect';
import AthleteStatusMultiSelect from 'components/form-fields/select-fields/AthleteStatusMultiSelect';
import HelpButton from 'components/HelpButton';
import dayjs from 'dayjs';
import useMissingAthletes from 'hooks/reports/athletes/useMissingAthletes';
import useMissingAthletesTotalCount from 'hooks/reports/athletes/useMissingAthletesTotalCount';
import { useNavigate } from 'react-router-dom';
import { MissingAthleteReport } from 'types/generated';

const useStyles = createStyles(theme => ({
  header: {
    width: '100%',
    background: theme.colors.gray[0],
    padding: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    position: 'relative',
    [theme.fn.smallerThan('md')]: {
      flexDirection: 'column',
      gap: theme.spacing.sm,
      padding: theme.spacing.md,
      justifyContent: 'flex-start',
      alignItems: 'start',
    },
  },
  filters: {
    display: 'flex',
    width: '80%',
    flex: '1 ',
    gap: '1.5rem',
    justifyContent: 'flex-end',
    [theme.fn.smallerThan('md')]: {
      width: '100%',
      justifyContent: 'flex-start',
      flexDirection: 'column',
      gap: '0.75rem',
    },
  },
  search1: {
    gridArea: 'search1',
    minWidth: '40%',
    maxWidth: 'calc(50% - 0.75rem)',
    [theme.fn.smallerThan('lg')]: {
      width: '50%',
    },
    [theme.fn.smallerThan('md')]: {
      width: '100%',
      maxWidth: '100%',
    },
  },
  search2: {
    gridArea: 'search2',
    minWidth: '40%',
    maxWidth: 'calc(50% - 0.75rem)',
    [theme.fn.smallerThan('lg')]: {
      width: '50%',
    },
    [theme.fn.smallerThan('md')]: {
      width: '100%',
      maxWidth: '100%',
    },
  },
  backButton: {
    gridArea: 'backButton',
  },
  title: {
    gridArea: 'title',
  },
  download: {
    gridArea: 'download',
    [theme.fn.smallerThan('md')]: {
      position: 'absolute',
      justifySelf: 'end',
      right: theme.spacing.md,
      top: '19.35px',
    },
    [theme.fn.smallerThan('sm')]: {
      display: 'none',
    },
  },
}));

const csvOptions = {
  fieldSeparator: ',',
  quoteStrings: '',
  decimalSeparator: '.',
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: true,
};

const dataToRow = (d: MissingAthleteReport): { [key: string]: any } => ({
  'Full Name': d.fullName,
  'Current Status': d.athleteStatus,
  Email: d.email,
  Phone: d.phone,
  'Last Check In Date': d.lastCheckInDate ? dayjs(d.lastCheckInDate).format('YYYY-MM-DD') : 'None',
  'Upcoming Reservation': d.nearestNextReservedOrWaitlistedDate
    ? dayjs(d.nearestNextReservedOrWaitlistedDate).format('YYYY-MM-DD')
    : 'None',
  'Current Packages': d.currentPackages.map(p => p.name).join(' and '),
});

interface FilterTerms {
  athleteStatuses: string[];
  packages: string[];
}

const MissingAthletesPageHeader = ({
  orderingValue,
  searchFilters,
  setSearchFilters,
}: {
  orderingValue: string;
  searchFilters: FilterTerms;
  setSearchFilters: React.Dispatch<React.SetStateAction<FilterTerms>>;
}): JSX.Element => {
  const theme = useMantineTheme();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const { classes } = useStyles();
  const navigate = useNavigate();

  const { data, isLoading, isSuccess, isError } = useMissingAthletesTotalCount({
    athleteStatuses: searchFilters?.athleteStatuses,
    packages: searchFilters?.packages,
  });

  const handleAthletePackageChange = (value: string[] | undefined): void => {
    setSearchFilters(prevValue => ({ ...prevValue, packages: value || [] }));
  };
  const handleAthleteStatusChange = (value: string[] | undefined): void => {
    setSearchFilters(prevValue => ({ ...prevValue, athleteStatuses: value || [] }));
  };

  const handleBackClick = useCallback((): void => {
    navigate(-1);
  }, [navigate]);

  const getMissingAthleteCount = (): JSX.Element => {
    if (isLoading) {
      return <Skeleton height='2rem' width='5rem' />;
    }
    if (isError) {
      return (
        <Title order={isDesktop ? 1 : 3} color='red'>
          Error
        </Title>
      );
    }
    if (isSuccess && data?.data?.count !== undefined) {
      return (
        <Title order={isDesktop ? 1 : 3} color='gray'>
          {data.data.count}
        </Title>
      );
    }
    return <></>;
  };

  return (
    <Paper className={classes.header}>
      <Group noWrap spacing='xl' className={classes.title} data-cy='missingAthletesPageHeader'>
        <BackButton handleOnClick={handleBackClick} />
        <Title order={isDesktop ? 1 : 3}>Missing Athletes</Title>
        {getMissingAthleteCount()}
        <HelpButton width={400} position={isDesktop ? 'right' : 'bottom'}>
          <Text size='sm'>
            Athletes that are not on hold and have not checked into a class or an appointment within the
            &quot;missing athlete&quot; amount of time. This amount of time can be configured on the settings
            page.
          </Text>
        </HelpButton>
      </Group>

      <Group noWrap className={classes.filters}>
        <PackageMultiSelect
          className={classes.search1}
          placeholder='Select packages'
          value={searchFilters?.packages}
          onChange={handleAthletePackageChange}
          clearable
        />

        <AthleteStatusMultiSelect
          className={classes.search2}
          placeholder='Select status'
          value={searchFilters?.athleteStatuses}
          onChange={handleAthleteStatusChange}
          clearable
          customOptions={['Trial', 'Non-Paying Member', 'Member']}
        />
      </Group>
      <DownloadCSV
        useList={useMissingAthletes}
        dataToRow={dataToRow}
        csvOptions={csvOptions}
        hookOptions={{
          ordering: orderingValue,
          packages: searchFilters?.packages,
          athleteStatuses: searchFilters?.athleteStatuses,
        }}
        className={classes.download}
        reportType='missing-athletes'
      />
    </Paper>
  );
};

export default MissingAthletesPageHeader;
